import { Component, signal, inject, input, effect, output, computed } from '@angular/core';
import { ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  AbstractControl,
} from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { SafeSelectComponent, SafeSelectOption } from '../safe-select/safe-select';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { I18nService } from '../../services/i18n.service';
import { ICountry } from '../../models/object.model';
import { CustomValidators } from '../../utilities/customer_validators';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NavButtonComponent } from '../nav-button/nav-button';
import { getName } from '../../utilities/global.util';
import { GLOBAL_CONSTANT } from '../../constants/global.constant';
import { LP_UI_CONFIG } from '../../core/tokens';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { FriendlyCaptchaWrapperComponent } from '../friendly-captcha/friendly-captcha-wrapper';
import { FormKey } from '../../enums/i18.enum';

export interface FormField {
  controlName: any;
  label: any;
  controlType: 'text' | 'number' | 'select' | 'password' | 'checkbox' | 'radio' | 'captcha';
  placeholder?: any;
  validators?: ValidatorFn[];
  options?: any;
  initialValue?: any;
}

@Component({
  selector: 'app-common-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    // NG-ZORRO Module
    NzIconModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzDatePickerModule,
    NzInputNumberModule,
    NzCheckboxModule,
    NzRadioModule,
    NzModalModule,
    NavButtonComponent,
    SafeSelectComponent,
    FriendlyCaptchaWrapperComponent,
  ],
  templateUrl: './common-form.html',
  styleUrl: './common-form.scss',
})
export class CommonFormComponent {
  public readonly cfg = inject(LP_UI_CONFIG);
  getName = getName;
  passwordControl: any;
  confirmPasswordContrl: any;
  formChanged: any;
  static buildForm(): any {
    throw new Error('Method not implemented.');
  }
  private router = inject(Router);
  private fb = inject(FormBuilder);
  public i18n = inject(I18nService);
  passwordVisibility: Record<string, boolean> = {};
  password?: string;
  formTitle = signal('User Registration');
  checkedTerms = false;
  checkedServiceConditions = false;
  checkedDataProtection = false;
  checked = false;
  formConfig = signal<FormField[]>([]);
  submittedData = signal<any | null>(null);
  public form: FormGroup;
  public FormKey = FormKey;

  formConfigureInput = input<FormField[]>([]);
  customerList = input<any[]>([]);
  isSuccessful = input<boolean>(false);
  submitText = input<string>(GLOBAL_CONSTANT.EMPTY_STRING);
  formType = input<string>('');
  captchaSiteKey = input<string>(this.cfg.captchaSitekey);
  isAdmin = input<boolean>(true);
  captchaEnabled = input<boolean>(this.cfg.enableCaptcha);
  hideTitle = input<boolean>(false);
  passwordMatchAlert = input<boolean>(false);
  countryList = input<ICountry[]>([]);

  countryOptions = computed<SafeSelectOption[]>(() =>
    this.countryList().map((c) => ({
      value: c.id,
      label: getName(c.name, this.i18n),
    }))
  );

  customerOptions = computed<SafeSelectOption[]>(() =>
    this.customerList().map((c) => ({ value: c.objectID, label: c.name }))
  );
  public route = inject(ActivatedRoute);
  private queryParams = toSignal(this.route.queryParams);
  country = computed(() => this.queryParams()?.['country'] ?? null);
  formSubmit = output<any>();
  formDenied = output<any>();
  isVisible = false;
  isLoading = signal(false);
  isPasswordMatched = signal(true);
  emailAlreadyRegistered = signal(false);
  error = signal(false);
  countrySelected = signal<any>(null);
  showNotUsPage = signal(true);
  submitted = signal(false);
  isWorking = signal(false);
  denialReason = GLOBAL_CONSTANT.EMPTY_STRING;
  @ViewChild(FriendlyCaptchaWrapperComponent)
  captchaComponent?: FriendlyCaptchaWrapperComponent;

  public resetCaptcha(): void {
    this.captchaComponent?.resetPuzzle();

    this.form.patchValue({
      captchaResponse: null,
    });

    this.form.get('captchaResponse')?.markAsUntouched();
    this.form.get('captchaResponse')?.updateValueAndValidity();
  }

  constructor() {
    this.form = this.fb.group({});
    effect(() => {
      this.formConfig.set(this.formConfigureInput());
      this.form = this.buildForm();
      this.passwordControl = this.form.get('password');
      this.confirmPasswordContrl = this.form.get('confirmPassword');
      this.form.valueChanges.subscribe(() => {
        this.formChanged.emit();
      });
    });
  }

  public buildForm(): FormGroup {
    const group: Record<string, AbstractControl> = {};
    this.formConfig().forEach((field) => {
      group[field.controlName] = this.fb.control(
        field.initialValue || null,
        field.validators || []
      );
    });
    const hasPasswordFields =
      this.formConfig().some((field) => field.controlName === 'password') &&
      this.formConfig().some((field) => field.controlName === 'confirmPassword');

    return this.fb.group(group, {
      validators: hasPasswordFields ? CustomValidators.getpasswordMatchValidator : [],
    });
  }

  checkButton(): void {
    this.checked = !this.checked;
  }

  hasLoginInUrl(): boolean {
    return this.router.url.includes('login');
  }

  onPasswordInput() {
    if (!this.hasLoginInUrl()) {
      if (this.confirmPasswordContrl.value === this.passwordControl.value) {
        this.isPasswordMatched.set(true);
      } else {
        this.isPasswordMatched.set(false);
      }
    }
  }

  showModal(): void {
    this.isVisible = true;
  }

  handleOk(): void {
    this.isVisible = false;
  }

  handleCancel(): void {
    this.isVisible = false;
  }

  deny() {
    this.formDenied.emit(this.denialReason);
    this.isVisible = false;
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.submittedData.set(this.form.value);
      this.formSubmit.emit(this.form.getRawValue());
    } else {
      Object.values(this.form.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
  }

  blurState: Record<string, boolean> = {};

  onBlur(field: string) {
    const value = this.form.get(field)?.value;
    this.blurState[field] = !value;
  }

  onInput(field: string) {
    this.blurState[field] = false;
  }

  get passwordInvalid() {
    return this.passwordControl?.hasError('required');
  }

  toTermsOfUse(): void {
    if (this.country() === 'us') {
      this.router.navigate(['us/terms-of-use']);
    } else {
      this.router.navigate(['/terms-of-use']);
    }
  }

  toDataProtectionDeclaration(): void {
    if (this.country() === 'us') {
      this.router.navigate(['us/data-protection']);
    } else {
      this.router.navigate(['/data-protection']);
    }
  }

  toServiceConditions() {
    this.router.navigate(['/service-conditions']);
  }
}
